/**
 * Tools: the tool servers (MCP servers) agents may use. What the organisation has published, what
 * machines are really running, and for each tool one of three words: Allowed, Ask first or Off.
 * A tool nobody has looked at, or whose words changed since someone did, waits for review here.
 */

import { RESULT_ORIGIN_WORDS, RISK_LEVEL_WORDS, TOOL_ACTION_LABEL, TOOL_RISKS, TOOL_RISK_LABEL, type ToolResultOrigin, toolServerEntrySchema, templateOfServer, type ToolAction, type ToolRisk, type ToolServerDefinition, type ToolServerTemplateView, type ToolServerView, type ToolView } from '@aioe/shared';
import { dateShort } from '@aioe/shared/wording/dateTime';
import { ArrowLeft, CircleQuestion, Display, Plus, Pulse, Wrench } from '@gravity-ui/icons';
import { Segment } from '@heroui-pro/react';
import { useState } from 'react';
import { listCount, ShowMore, useListSearch, usePagedList } from '../pagedList.tsx';
import { SearchBox } from '../components/ui/SearchBox.tsx';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { problemText, routes } from '../api/client.ts';
import { Button, ChoiceSelect, Empty, ErrorNote, inputClass, ListBlock, ListGroup, ListRow, PageColumn, PageHeader, Stat, StatusMark } from '../components/ui.tsx';
import { FormField, FormSheet } from '../components/ui/FormSheet.tsx';
import { Explainer } from '../components/ui/Layout.tsx';
import { RowMenu } from '../components/ui/RowMenu.tsx';
import { VIEW_TONES } from '../components/ui/Art.tsx';
import { timeAgo, useLoad } from '../hooks.ts';
import { plainText } from '../components/Markdown.tsx';
import { WholeNumber } from '../components/updatesFields.tsx';
import { NamedByRepositories } from '../components/projectCode.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { isWebService, ScanFindings, ServerVerification, VerifyDialog } from '../components/CatalogueChecks.tsx';

const tone = VIEW_TONES.tools!;
const CLASS_DEFAULT: Record<ToolRisk, ToolAction> = { reads: 'allow', changes: 'ask', destroys: 'off' };
const CLASS_SENTENCE: Record<ToolRisk, string> = { reads: 'Tools that only read. Allowed unless you say otherwise.', changes: 'Tools that change something. A person is asked first unless you say otherwise.', destroys: 'Tools that delete or cannot be undone. Off unless you say otherwise.' };
const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const where = (d: ToolServerDefinition) => (d.transport === 'stdio' ? 'Runs on the machine' : 'A web service');
const line = (d: ToolServerDefinition) => (d.transport === 'stdio' ? [d.command, ...d.args].join(' ') : (d.url ?? ''));
/** The organisation's "After outside content" setting (Policies, Tools agents may use), in one sentence. */
const AFTER_OUTSIDE_NOW: Record<'off' | 'record' | 'ask', string> = {
  off: 'Once a session has read outside content, nothing changes.',
  record: 'Once a session has read outside content, its calls are marked so in the audit trail.',
  ask: 'Once a session has read outside content, a person is asked before a tool changes, deletes or reads private data.',
};
/** Where a tool's answers come from, set in one choice: whose words, and whether they are private. */
type Answers = ToolResultOrigin | 'outside-private';
const ANSWERS: Record<Answers, string> = { own: 'From the workbench itself', repository: 'From the repository', outside: 'From outside the organisation', 'outside-private': 'From outside, with private data' };
const answersOf = (t: ToolView): Answers | undefined => (t.resultOrigin === 'outside' && t.readsPrivate ? 'outside-private' : t.resultOrigin);
/** The two facts in words, for the tool's row: nothing while nobody has said. */
const answerWords = (t: ToolView) => [t.resultOrigin ? RESULT_ORIGIN_WORDS[t.resultOrigin] : undefined, t.readsPrivate ? 'Reads private data' : undefined].filter(Boolean).join(' · ');
const slug = (name: string) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

export function ToolsPage() {
  const { fingerprint } = useParams();
  const navigate = useNavigate();
  // The published servers are whole; those only a repository names ("Seen in your fleet") are searched and paged on the server.
  const search = useListSearch();
  const seen = usePagedList((cursor) => routes.toolsPage({ q: search.q, ...(fingerprint ? { fingerprint } : {}), cursor }), { q: search.q, fingerprint }, { intervalMs: 30_000, keyOf: (s) => s.fingerprint });
  const tools = { data: seen.loaded && seen.extra ? { servers: [...seen.extra.servers, ...seen.items], waiting: seen.extra.waiting, afterOutside: seen.extra.afterOutside } : undefined, error: seen.error, reload: seen.reload, loading: seen.loading };
  const templates = useLoad(() => routes.toolTemplates(), []);
  const named = useLoad(() => routes.toolsNamedByRepositories().catch(() => undefined), [], 60_000);
  const [adding, setAdding] = useState<ToolServerView | 'new'>();
  const [publishing, setPublishing] = useState<ToolServerTemplateView>();
  const servers = tools.data?.servers ?? [];
  const selected = fingerprint ? servers.find((s) => s.fingerprint === fingerprint) : undefined;
  const addSheet = adding ? <AddServer from={adding === 'new' ? undefined : adding} onDone={() => (setAdding(undefined), tools.reload())} onCancel={() => setAdding(undefined)} /> : null;

  if (fingerprint) {
    return (
      <PageColumn width="form">
        <ErrorNote error={tools.error} />
        {selected ? (
          <ServerDetail server={selected} reload={tools.reload} onAdopt={() => setAdding(selected)} />
        ) : tools.data ? (
          <>
            <PageHeader tone={tone} icon={<Wrench />} title="Tool server not found" subtitle="It may have been removed, or no machine has reported it yet." />
            <Empty
              tone={tone}
              icon={<Wrench />}
              title="No such tool server"
              body="It may have been removed, or no machine has reported it yet."
              action={
                <Button size="sm" variant="secondary" onPress={() => navigate('/tools')}>
                  Back to Tools
                </Button>
              }
            />
          </>
        ) : null}
        {addSheet}
      </PageColumn>
    );
  }

  const published = servers.filter((s) => s.status !== 'unlisted');
  const unlisted = servers.filter((s) => s.status === 'unlisted');
  const calls = servers.reduce((n, s) => n + s.tools.reduce((m, t) => m + t.calls, 0), 0);
  // A template with parameters stays ready: each publish is another knowledge base or data agent.
  const ready = (templates.data ?? []).filter((t) => !t.catalogueId);
  const nothing = tools.data && servers.length === 0 && ready.length === 0;
  return (
    <PageColumn width="data">
      <PageHeader
        tone={tone}
        icon={<Wrench />}
        title="Tools"
        subtitle="The tool servers your agents may use, what each tool is allowed to do, and what machines are really running."
        actions={
          <Button size="sm" variant="primary" onPress={() => setAdding('new')}>
            <Plus /> Add a tool server
          </Button>
        }
      />
      <Explainer id="tools" tone={tone} icon={<Wrench />} title="How tools work" summary="Every tool has one of three words: Allowed, Ask first or Off">
        <p>A tool server gives agents tools: an issue tracker, a database, a browser. Publish one here and every workbench and node gets it; machines also report the servers they start from a repository’s own configuration, so you can adopt what people already use.</p>
        <p>Each tool is classed by what it does (reads, changes or destroys) and that class decides its default: allowed, ask a person first, or off. Open a server to change a tool’s word, correct its class or say where its answers come from.</p>
        <p>A tool nobody has looked at, or whose description changed since someone did, waits for review; until then a tool that changes things asks a person first.</p>
      </Explainer>
      <PartBoundary key="tools" name="Tools" tone={tone}>
        <ErrorNote error={tools.error} />
        <div className="art-stats">
          <Stat tone={tone} icon={<Wrench />} label="Published" value={tools.data ? published.filter((s) => s.status === 'published').length : undefined} detail={published.some((s) => s.status === 'withdrawn') ? `${plural(published.filter((s) => s.status === 'withdrawn').length, 'server')} withdrawn` : 'Every machine may start them'} />
          <Stat tone={tone} icon={<CircleQuestion />} label="Waiting for review" value={tools.data?.waiting} detail={tools.data?.waiting ? 'New or changed tools' : 'Every tool has been looked at'} needsYou={!!tools.data?.waiting} />
          <Stat tone={tone} icon={<Display />} label="Seen, not published" value={tools.data ? (seen.extra?.unlistedTotal ?? unlisted.length) : undefined} detail={unlisted.length ? 'Started from a repository' : 'Nothing unpublished is running'} />
          <Stat tone={tone} icon={<Pulse />} label="Calls in 30 days" value={tools.data ? calls : undefined} detail="Calls let through" />
        </div>

        {nothing ? (
          <Empty
            tone={tone}
            icon={<Wrench />}
            title="No tool servers yet"
            body="A tool server gives agents tools: an issue tracker, a database, a browser. Machines report the ones they start here, so you can adopt what people already use. Or add one yourself."
            action={
              <Button size="sm" variant="primary" onPress={() => setAdding('new')}>
                Add a tool server
              </Button>
            }
          />
        ) : null}

        {published.length > 0 && (
          <ListGroup title="Published" count={published.length} description={`Every workbench and node gets these. Open one to decide what its tools may do. ${AFTER_OUTSIDE_NOW[tools.data?.afterOutside ?? 'record']}`}>
            {published.map((s) => (
              <ListRow
                key={s.fingerprint}
                to={`/tools/${s.fingerprint}`}
                icon={<Wrench />}
                title={s.name}
                description={
                  <>
                    <span className="line-clamp-1">{s.summary ?? line(s.definition)}</span>
                    <span className="block tabular-nums">
                      {where(s.definition)} · {plural(s.tools.length, 'tool')} · {s.machines ? `on ${plural(s.machines, 'machine')}` : 'no machine has started it yet'}
                    </span>
                  </>
                }
                status={s.status === 'withdrawn' ? <StatusMark state="disabled" label="Withdrawn" /> : s.waiting > 0 ? <StatusMark state="medium" label={`${s.waiting} to review`} /> : undefined}
              />
            ))}
          </ListGroup>
        )}

        {ready.length > 0 && (
          <ListGroup title="Ready to publish" count={ready.length} description="Servers already known here. Publishing one classifies its tools, so your defaults apply from the first call.">
            {ready.map((t) => {
              const reads = t.tools.filter((x) => x.risk === 'reads').length;
              return (
                <ListRow
                  key={t.id}
                  icon={<Wrench />}
                  title={t.name}
                  description={
                    <>
                      <span className="line-clamp-2">{plainText(t.summary)}</span>
                      <span className="block">
                        A web service · {plural(t.tools.length, 'tool')}: {reads} read, {t.tools.length - reads} change or delete
                        {t.definition.auth ? ' · each person signs in as themselves' : ''}
                        {t.published?.length ? ` · published as ${t.published.map((x) => x.name).join(', ')}` : ''}
                      </span>
                    </>
                  }
                  control={
                    <Button size="sm" variant="secondary" onPress={() => setPublishing(t)}>
                      {t.published?.length ? 'Publish another' : 'Publish'}
                    </Button>
                  }
                />
              );
            })}
          </ListGroup>
        )}

        {(named.data?.servers.length ?? 0) > 0 && (
          <ListGroup title="Named by repositories but not approved" count={named.data!.servers.length} description="A repository’s own files list these servers. Publish one to approve it, or leave it unapproved.">
            <ListBlock flush>
              <NamedByRepositories data={named.data!} />
            </ListBlock>
          </ListGroup>
        )}

        {(unlisted.length > 0 || search.q) && (
          <ListGroup
            title="Seen in your fleet"
            count={listCount(unlisted.length, { one: 'server', many: 'servers' }, { total: search.q ? undefined : seen.extra?.unlistedTotal, hasMore: seen.hasMore, searched: search.q !== '' })}
            description="Machines start these from a repository’s own configuration. Nobody has published them."
            actions={(seen.extra?.unlistedTotal ?? 0) > 6 || search.q ? <SearchBox label="Search unpublished servers" value={search.typed} onChange={search.setTyped} placeholder="Search servers" className="w-40 max-w-full @lg:w-56" /> : undefined}
          >
            {unlisted.map((s) => (
              <ListRow
                key={s.fingerprint}
                icon={<Display />}
                title={
                  <Link to={`/tools/${s.fingerprint}`} className="hover:underline">
                    {s.name}
                  </Link>
                }
                description={
                  <>
                    <span className="mono block truncate" title={line(s.definition)}>
                      {line(s.definition)}
                    </span>
                    <span className="block">
                      {where(s.definition)} · on {plural(s.machines, 'machine')}
                      {s.projectIds.length ? ` · ${plural(s.projectIds.length, 'project')}` : ''} · {plural(s.tools.length, 'tool')}
                      {s.lastSeenAt ? ` · seen ${timeAgo(s.lastSeenAt)}` : ''}
                    </span>
                  </>
                }
                control={
                  <Button size="sm" variant="secondary" onPress={() => setAdding(s)}>
                    Adopt
                  </Button>
                }
              />
            ))}
          {seen.hasMore ? <ShowMore onPress={seen.showMore} pending={seen.loadingMore} /> : null}
          </ListGroup>
        )}
      </PartBoundary>
      {addSheet}
      {publishing ? <PublishTemplate template={publishing} onClose={() => setPublishing(undefined)} onPublished={() => (templates.reload(), tools.reload())} /> : null}
    </PageColumn>
  );
}

/** Publishing a server AIOE knows how to publish: what it is, what it needs, and what to do first. */
function PublishTemplate({ template: t, onClose, onPublished }: { template: ToolServerTemplateView; onClose: () => void; onPublished: () => void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const params = t.parameters ?? [];
  const reads = t.tools.filter((x) => x.risk === 'reads').length;
  const filled = params.every((p) => new RegExp(p.pattern).test((values[p.name] ?? '').trim()));
  const publish = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const s = await routes.publishToolTemplate(t.id, params.length ? values : undefined);
      onPublished();
      onClose();
      navigate(`/tools/${s.fingerprint}`);
    } catch (e) {
      setError(problemText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <FormSheet
      title={t.published?.length ? `Publish another ${t.name}` : `Publish ${t.name}`}
      description="Every workbench and node gets it on its next sync, with its tools already classed."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={busy} isDisabled={!filled} onPress={() => void publish()}>
            Publish
          </Button>
        </>
      }
    >
      <p className="text-sm">{plainText(t.summary)}</p>
      <p className="text-xs text-muted">
        A web service · {plural(t.tools.length, 'tool')}: {reads} read, {t.tools.length - reads} change or delete
        {t.definition.auth ? ' · each person signs in as themselves' : ''}
      </p>
      {t.published?.length ? <p className="text-xs text-muted">Already published: {t.published.map((p) => p.name).join(', ')}</p> : null}
      {params.map((p) => (
        <FormField key={p.name} label={p.label} hint={p.hint}>
          <input className={`${inputClass} mono`} value={values[p.name] ?? ''} placeholder={p.example} spellCheck={false} onChange={(e) => setValues({ ...values, [p.name]: e.target.value })} />
        </FormField>
      ))}
      <a href={t.docs} target="_blank" rel="noreferrer" className="w-fit text-xs text-muted underline-offset-2 hover:text-foreground hover:underline">
        What Microsoft says about it
      </a>
      <ErrorNote error={error} />
    </FormSheet>
  );
}

/** Hermes item 1: what a node found when it started the server, against what the entry declares; Verify asks a node again. */
function WhatItRegisters({ catalogueId, name }: { catalogueId: string; name: string }) {
  const entries = useLoad(() => routes.toolServerEntries(), [catalogueId]);
  const [verifying, setVerifying] = useState(false);
  const e = entries.data?.entries.find((x) => x.id === catalogueId);
  if (!e) return null;
  const declared = toolServerEntrySchema.safeParse(e.content);
  const names = declared.success ? (declared.data.tools ?? []) : [];
  const web = isWebService(e.content);
  return (
    <ListGroup
      title="What it registers"
      description={names.length ? `It declares ${plural(names.length, 'tool')}; ${web ? 'that is checked' : 'a node checks that'} against what the server really lists.` : 'It declares no tools.'}
      actions={
        <Button size="sm" variant="ghost" isDisabled={!web && e.verifying?.state === 'pending'} onPress={() => setVerifying(true)}>
          Verify
        </Button>
      }
    >
      <ListBlock className="grid gap-2">
        <ServerVerification verified={e.verified} verifying={e.verifying} declared={names} web={web} />
        {names.length > 0 && <div className="mono break-words text-xs text-muted">{names.join(', ')}</div>}
        <ScanFindings scan={e.scan} />
      </ListBlock>
      {verifying && <VerifyDialog target={{ kind: 'entry', id: catalogueId, name }} web={web} onClose={() => setVerifying(false)} onIssued={() => void entries.reload()} />}
    </ListGroup>
  );
}

/** For a server published from a template: what an administrator does elsewhere before it answers. */
function BeforeItAnswers({ catalogueId }: { catalogueId: string }) {
  const t = templateOfServer(catalogueId);
  if (!t) return null;
  return (
    <ListGroup title="Before it answers" description={`Done in ${t.provider}, not here.`}>
      <ListBlock>
        <ol className="grid list-decimal gap-1.5 pl-5 text-sm [overflow-wrap:anywhere]">
          {t.before.map((step) => (
            <li key={step}>{step}</li>
          ))}
        </ol>
        <a href={t.docs} target="_blank" rel="noreferrer" className="mt-3 inline-block text-xs text-muted hover:text-foreground">
          Microsoft's documentation
        </a>
      </ListBlock>
    </ListGroup>
  );
}

/* ---------- one server ---------- */

type Review = (names: string[], patch: { action?: ToolAction | null; risk?: ToolRisk; riskLevel?: number; resultOrigin?: ToolResultOrigin; readsPrivate?: boolean; reviewed?: true }) => Promise<void>;

function ServerDetail({ server: s, reload, onAdopt }: { server: ToolServerView; reload: () => void; onAdopt: () => void }) {
  const navigate = useNavigate();
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<string>();
  const listed = s.status !== 'unlisted';
  const run = async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError(undefined);
    try {
      await work();
      reload();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  const review: Review = (names, patch) => run(() => routes.reviewTools(s.fingerprint, { names, ...patch }));
  const setStatus = (status: 'published' | 'withdrawn') => run(() => routes.saveCatalogue({ id: s.catalogueId!, kind: 'mcp', name: s.name, ...(s.summary ? { summary: s.summary } : {}), content: { ...s.definition, status } }));
  const waiting = s.tools.filter((t) => t.status !== 'reviewed');
  const opened = open ? s.tools.find((t) => t.name === open) : undefined;
  const mayDo = (t: ToolView, risk: ToolRisk) => (
    <ChoiceSelect
      label={`What ${t.name} may do`}
      className="w-48 max-w-full"
      value={t.action ?? 'default'}
      isDisabled={busy}
      options={{ default: `Class default (${TOOL_ACTION_LABEL[CLASS_DEFAULT[risk]]})`, ...TOOL_ACTION_LABEL }}
      onChange={(v) => void review([t.name], { action: v === 'default' ? null : (v as ToolAction) })}
    />
  );
  return (
    <>
      <PageHeader
        tone={tone}
        icon={<Wrench />}
        title={s.name}
        subtitle={s.summary ?? (listed ? 'A tool server your organisation has published.' : 'Machines start this server from a repository. It is not in your catalogue.')}
        actions={
          <>
            <Button size="sm" variant="ghost" onPress={() => navigate('/tools')}>
              <ArrowLeft className="size-4" />
              Tools
            </Button>
            {s.status === 'withdrawn' && (
              <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => void setStatus('published')}>
                Publish again
              </Button>
            )}
            {!listed && (
              <Button size="sm" variant="primary" isDisabled={busy} onPress={onAdopt}>
                Adopt
              </Button>
            )}
            {listed && (
              <RowMenu
                label={`More for ${s.name}`}
                items={[s.status === 'published' && { id: 'withdraw', label: 'Withdraw: every tool off on every machine', disabled: busy }, s.status === 'withdrawn' && { id: 'remove', label: 'Remove from the catalogue', danger: true, disabled: busy }]}
                onAction={(id) => (id === 'withdraw' ? void setStatus('withdrawn') : void run(() => routes.deleteCatalogue(s.catalogueId!)).then(() => navigate('/tools')))}
              />
            )}
          </>
        }
      />
      <ErrorNote error={error} />
      <div className="art-stats">
        <Stat tone={tone} icon={<Wrench />} label="Status" value={s.status === 'published' ? 'Published' : s.status === 'withdrawn' ? 'Withdrawn' : 'Not published'} detail={s.status === 'withdrawn' ? 'Every tool is off, on every machine' : s.status === 'published' ? 'Machines may start it' : 'Your policy decides what happens to it'} />
        <Stat tone={tone} icon={<CircleQuestion />} label="Waiting for review" value={listed ? waiting.length : '—'} detail={listed ? (waiting.length ? 'New or changed since someone looked' : 'Every tool has been looked at') : 'Adopt it to review its tools'} needsYou={listed && waiting.length > 0} />
        <Stat tone={tone} icon={<Display />} label="Machines" value={s.machines} detail={s.lastSeenAt ? `Last seen ${timeAgo(s.lastSeenAt)}${s.projectIds.length ? ` · ${plural(s.projectIds.length, 'project')}` : ''}` : 'No machine has started it yet'} />
      </div>
      {s.catalogueId && <BeforeItAnswers catalogueId={s.catalogueId} />}
      {s.catalogueId && <WhatItRegisters catalogueId={s.catalogueId} name={s.name} />}
      <ListGroup title="How it is started" description={where(s.definition)}>
        <ListBlock className="grid gap-1 text-sm">
          <div className="mono break-all">{line(s.definition)}</div>
          {(s.definition.env.length > 0 || s.definition.headers.length > 0) && (
            <div className="text-xs text-muted">
              Needs <span className="mono">{[...s.definition.env, ...s.definition.headers].join(', ')}</span> from the machine. The value is never kept here.
            </div>
          )}
          {s.alsoReadBy?.length ? <div className="text-xs text-muted">{s.alsoReadBy.join(', ')} also read this server from the repository's own configuration, so what the organisation decides reaches them only through the credentials the gate holds.</div> : null}
          {s.masked && <div className="text-xs text-warning">A machine starts it with what looks like a credential typed into the definition. It has been masked here. Move it to an environment variable before adopting the server.</div>}
          {s.error && <div className="text-xs text-danger">The last machine to try could not list its tools: {s.error}</div>}
        </ListBlock>
      </ListGroup>

      {listed && waiting.length > 0 && (
        <ListGroup
          title="Waiting for review"
          count={waiting.length}
          description="Until someone looks, agents see the words that were reviewed, and a tool that changes things asks a person first."
          actions={
            <Button size="sm" variant="secondary" isDisabled={busy} onPress={() => void review(waiting.map((t) => t.name), { reviewed: true })}>
              Mark all reviewed
            </Button>
          }
        >
          {waiting.map((t) => (
            <ListRow
              key={t.name}
              title={<span className="mono">{t.name}</span>}
              description={
                <>
                  <span className="block">{t.status === 'changed' ? 'Its description changed since it was reviewed' : 'New: nobody has looked at it yet'} · {TOOL_RISK_LABEL[t.risk]}</span>
                  {t.description ? <span className="line-clamp-2">{t.description}</span> : null}
                </>
              }
              control={
                <Button size="sm" variant="secondary" onPress={() => setOpen(t.name)}>
                  Review
                </Button>
              }
            />
          ))}
        </ListGroup>
      )}

      {s.tools.length === 0 ? (
        <Empty tone={tone} icon={<Wrench />} title="No tools reported yet" body="A machine lists a server's tools the first time it starts it. They appear here then." />
      ) : (
        TOOL_RISKS.map((risk) => {
          const group = s.tools.filter((t) => t.risk === risk);
          if (!group.length) return null;
          return (
            <ListGroup
              key={risk}
              title={TOOL_RISK_LABEL[risk]}
              count={group.length}
              description={CLASS_SENTENCE[risk]}
              actions={
                listed ? (
                  <ChoiceSelect
                    label={`Set every ${TOOL_RISK_LABEL[risk]} tool`}
                    className="w-36"
                    value={undefined}
                    placeholder="Set all…"
                    isDisabled={busy}
                    options={{ default: `Class default (${TOOL_ACTION_LABEL[CLASS_DEFAULT[risk]]})`, ...TOOL_ACTION_LABEL }}
                    onChange={(v) => void review(group.map((t) => t.name), { action: v === 'default' ? null : (v as ToolAction) })}
                  />
                ) : undefined
              }
            >
              {group.map((t) => {
                const facts = [`Risk level ${t.riskLevel} out of 5, ${RISK_LEVEL_WORDS[t.riskLevel as 1 | 2 | 3 | 4 | 5] ?? ''}`, answerWords(t), t.risk !== t.reportedRisk ? `class corrected: the server says ${TOOL_RISK_LABEL[t.reportedRisk]}` : '', `${plural(t.calls, 'call')} in 30 days`, t.reviewedBy && t.reviewedAt ? `reviewed by ${t.reviewedBy}, ${dateShort(t.reviewedAt)}` : ''].filter(Boolean);
                return (
                  <ListRow
                    key={t.name}
                    title={<span className="mono">{t.name}</span>}
                    description={
                      <>
                        {t.description ? <span className="line-clamp-2">{t.status === 'changed' && t.reviewedDescription !== undefined ? t.reviewedDescription : t.description}</span> : null}
                        <span className="block tabular-nums">{facts.join(' · ')}</span>
                      </>
                    }
                    control={
                      listed ? (
                        <>
                          {mayDo(t, risk)}
                          <RowMenu label={`More for ${t.name}`} items={[{ id: 'open', label: 'Class, risk and answers…' }]} onAction={() => setOpen(t.name)} />
                        </>
                      ) : undefined
                    }
                  />
                );
              })}
            </ListGroup>
          );
        })
      )}
      {opened && listed ? <ToolSheet tool={opened} busy={busy} review={review} onClose={() => setOpen(undefined)} /> : null}
    </>
  );
}

/** One tool's review: what the agent is told (and what was reviewed, when it changed), its class, its risk and where its answers come from. */
function ToolSheet({ tool: t, busy, review, onClose }: { tool: ToolView; busy: boolean; review: Review; onClose: () => void }) {
  const waiting = t.status !== 'reviewed';
  const setAnswers = (a: Answers) => void review([t.name], a === 'outside-private' ? { resultOrigin: 'outside', readsPrivate: true } : { resultOrigin: a, readsPrivate: false });
  return (
    <FormSheet
      title={t.name}
      description={waiting ? (t.status === 'changed' ? 'Its description changed since someone reviewed it.' : 'New: nobody has looked at it yet.') : 'Each change applies at once.'}
      onClose={onClose}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onClose}>
            Close
          </Button>
          {waiting ? (
            <Button size="sm" variant="primary" isDisabled={busy} onPress={() => void review([t.name], { reviewed: true }).then(onClose)}>
              Mark reviewed
            </Button>
          ) : null}
        </>
      }
    >
      {t.status === 'changed' && t.reviewedDescription !== undefined ? (
        <>
          <Words label="What was reviewed" text={t.reviewedDescription} />
          <Words label="What the server says now" text={t.description} />
        </>
      ) : (
        <Words label="What the agent is told" text={t.description} />
      )}
      <FormField label="Class" hint={t.risk !== t.reportedRisk ? `Corrected: the server says ${TOOL_RISK_LABEL[t.reportedRisk]}.` : 'What the server says about itself. Correct it if it is wrong.'}>
        <ChoiceSelect label={`Class of ${t.name}`} className="w-full" value={t.risk} isDisabled={busy} options={Object.fromEntries(TOOL_RISKS.map((r) => [r, TOOL_RISK_LABEL[r]])) as Record<ToolRisk, string>} onChange={(risk) => void review([t.name], { risk })} />
      </FormField>
      <FormField label="Risk level" hint={`${t.riskLevel} out of 5: ${RISK_LEVEL_WORDS[t.riskLevel as 1 | 2 | 3 | 4 | 5] ?? ''}`}>
        <WholeNumber value={t.riskLevel} min={1} max={5} isDisabled={busy} ariaLabel={`Risk level of ${t.name}, 1 to 5`} onChange={(riskLevel) => void review([t.name], { riskLevel })} />
      </FormField>
      <FormField label="Where its answers come from" hint="A session that reads an answer from outside is marked, and private reads are asked about after it.">
        <ChoiceSelect label={`Where the answers of ${t.name} come from`} className="w-full" value={answersOf(t)} placeholder="Not said" isDisabled={busy} options={ANSWERS} onChange={setAnswers} />
      </FormField>
    </FormSheet>
  );
}

function Words({ label, text }: { label: string; text?: string }) {
  return (
    <div className="rounded-lg bg-surface-secondary p-3">
      <div className="text-xs text-muted">{label}</div>
      <p className="whitespace-pre-wrap break-words text-sm">{text || 'The server gives no description.'}</p>
    </div>
  );
}

/* ---------- adding or adopting a server ---------- */

interface Draft {
  name: string;
  summary: string;
  transport: 'stdio' | 'http';
  command: string;
  url: string;
  credentials: string;
}

/** A pasted `.mcp.json` entry, whole file or one server: what it says, with every credential value dropped and only its name kept. */
function fromPaste(text: string): Partial<Draft> | string {
  let doc: unknown;
  try {
    doc = JSON.parse(text);
  } catch {
    return 'That is not JSON. Paste the entry from a .mcp.json file.';
  }
  const servers = (doc as { mcpServers?: Record<string, unknown>; servers?: Record<string, unknown> }).mcpServers ?? (doc as { servers?: Record<string, unknown> }).servers;
  const [name, raw] = servers ? (Object.entries(servers)[0] ?? []) : [undefined, doc];
  const s = raw as { command?: string; args?: string[]; url?: string; env?: Record<string, unknown>; headers?: Record<string, unknown> } | undefined;
  if (!s || (typeof s.command !== 'string' && typeof s.url !== 'string')) return 'No server found in that: it needs a command or a url.';
  const credentials = [...Object.keys(s.env ?? {}), ...Object.keys(s.headers ?? {})].join(', ');
  return { ...(name ? { name } : {}), transport: s.url ? 'http' : 'stdio', command: [s.command ?? '', ...(Array.isArray(s.args) ? s.args : [])].join(' ').trim(), url: s.url ?? '', credentials };
}

function AddServer({ from, onDone, onCancel }: { from?: ToolServerView; onDone: () => void; onCancel: () => void }) {
  const [d, setD] = useState<Draft>(() => ({
    name: from?.name ?? '',
    summary: '',
    transport: from?.definition.transport ?? 'stdio',
    command: from?.definition.transport === 'stdio' ? line(from.definition) : '',
    url: from?.definition.url ?? '',
    credentials: from ? [...from.definition.env, ...from.definition.headers].join(', ') : '',
  }));
  const [paste, setPaste] = useState('');
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const names = d.credentials.split(/[\s,]+/).filter(Boolean);
  const save = async () => {
    setError(undefined);
    // An adopted server keeps the exact arguments machines reported, so it is recognised as the same server.
    const [command, ...typed] = d.command.trim().split(/\s+/);
    const args = from?.definition.transport === 'stdio' && d.command.trim() === line(from.definition) ? from.definition.args : typed;
    const content = toolServerEntrySchema.safeParse(d.transport === 'stdio' ? { transport: 'stdio', command, args, env: names } : { transport: 'http', url: d.url.trim(), headers: names });
    if (!content.success) {
      setError(d.transport === 'stdio' ? 'Give the command that starts the server.' : 'Give the address of the server.');
      return;
    }
    setSaving(true);
    try {
      await routes.saveCatalogue({ id: slug(d.name), kind: 'mcp', name: d.name.trim(), ...(d.summary.trim() ? { summary: d.summary.trim() } : {}), content: content.data });
      onDone();
    } catch (e) {
      const body = (e as { body?: { message?: string } }).body;
      setError(body?.message ?? (e as Error).message);
    } finally {
      setSaving(false);
    }
  };
  return (
    <FormSheet
      title={from ? `Adopt ${from.name}` : 'Add a tool server'}
      description={from ? 'Publish it as your organisation’s, so you can decide what its tools may do.' : 'Every workbench and node gets it on its next sync.'}
      onClose={onCancel}
      footer={
        <>
          <Button size="sm" variant="tertiary" onPress={onCancel}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" isPending={saving} isDisabled={!slug(d.name)} onPress={() => void save()}>
            Publish
          </Button>
        </>
      }
    >
      {!from && (
        <FormField label="Paste from a .mcp.json file (optional)" hint="Fills in the fields below. Credential values are dropped; only their names are kept.">
          <textarea
            className={`${inputClass} mono min-h-20 text-xs`}
            value={paste}
            spellCheck={false}
            placeholder={'{ "mcpServers": { "tracker": { "command": "npx", "args": ["-y", "@example/tracker-mcp"], "env": { "TRACKER_TOKEN": "…" } } } }'}
            onChange={(e) => {
              setPaste(e.target.value);
              if (!e.target.value.trim()) return setError(undefined);
              const got = fromPaste(e.target.value);
              if (typeof got === 'string') setError(got);
              else {
                setError(undefined);
                set(got);
              }
            }}
          />
        </FormField>
      )}
      <FormField label="Name">
        <input className={inputClass} value={d.name} onChange={(e) => set({ name: e.target.value })} placeholder="Issue tracker" />
      </FormField>
      <FormField label="What it is for, in a sentence">
        <input className={inputClass} value={d.summary} onChange={(e) => set({ summary: e.target.value })} placeholder="Read and update issues in the tracker." />
      </FormField>
      <FormField label="Where it runs">
        <Segment aria-label="Where it runs" size="sm" selectedKey={d.transport} onSelectionChange={(k) => !from && set({ transport: String(k) as Draft['transport'] })} className="w-fit">
          <Segment.Item id="stdio">Runs on the machine</Segment.Item>
          <Segment.Item id="http">A web service</Segment.Item>
        </Segment>
      </FormField>
      {d.transport === 'stdio' ? (
        <FormField label="The command that starts it">
          <input className={`${inputClass} mono`} value={d.command} readOnly={!!from} onChange={(e) => set({ command: e.target.value })} placeholder="npx -y @example/tracker-mcp@1.4.0" />
        </FormField>
      ) : (
        <FormField label="Its address">
          <input className={`${inputClass} mono`} value={d.url} readOnly={!!from} onChange={(e) => set({ url: e.target.value })} placeholder="https://mcp.example.com/mcp" />
        </FormField>
      )}
      <FormField label={d.transport === 'stdio' ? 'Environment variables it needs, by name' : 'Headers it needs, by name'} hint="Each machine supplies the value. A credential is never kept here, and one typed into the command or the address is refused.">
        <input className={`${inputClass} mono`} value={d.credentials} onChange={(e) => set({ credentials: e.target.value })} placeholder={d.transport === 'stdio' ? 'TRACKER_TOKEN' : 'Authorization'} />
      </FormField>
      {from?.masked && <p className="text-xs text-warning">The machines that start this server have a credential typed into its definition. Published as it is, it will not be recognised as the same server: move the credential to an environment variable in the repository first.</p>}
      <ErrorNote error={error} />
    </FormSheet>
  );
}
